import { useContext } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { deleteFileUploadTask, removeFileProcessing, FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { selectConversationSelectedFiles, selectProcessingStatusesForSelectedFiles } from '@/chat-common/selected_files/store/selectors'
import FilePill from './FilePill'
import UserFilePill from './UserFilePill'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { AlertErrorCircle } from '@/assets/icons'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import {
  selectAgentConversationFormFilePaths,
  selectAgentConversationFormPendingUserFileUploads,
  selectAgentConversationFormTemplateFilePaths,
  selectAgentConversationFormUserFileIds,
  selectAgentConversationFormWritingSampleFileIds,
} from '@/agent/chat_form/store/selectors'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { getUserFileAttachmentStatus } from '@/chat-common/selected_files/utils/userFileUploadStatus'
import { FileUploadContext } from '@/context/file-upload-context'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'

type SelectedFilesProps = {
  chatId: string
  isOverLimit: boolean
  showLegacyFilesResetNotice?: boolean
}

export default function SelectedFilesContent(props: SelectedFilesProps) {
  const { chatId, isOverLimit, showLegacyFilesResetNotice = false } = props
  const dispatch = useAppDispatch()
  const viewUserFile = useViewUserFile()
  const { cancelUserFileUpload } = useContext(FileUploadContext)

  // All selected files (non-template and template)
  const selectedFilePaths = useAppSelector((state: RootState) => selectConversationSelectedFiles(state, { conversationId: chatId }))

  // Non-template files
  const selectFilePaths = useAppSelector((state: RootState) => selectAgentConversationFormFilePaths(state, { conversationId: chatId }))

  // Template files
  const templateFiles = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId: chatId }))

  // Matter file IDs (new user_files flow)
  const userFileIds = useAppSelector((state: RootState) => selectAgentConversationFormUserFileIds(state, { conversationId: chatId }))

  // Writing-sample file IDs (new user_files flow) — rendered as template-style chips
  const writingSampleFileIds = useAppSelector((state: RootState) => selectAgentConversationFormWritingSampleFileIds(state, { conversationId: chatId }))
  const { filesById, error: fetchError } = useUserFiles([...userFileIds, ...writingSampleFileIds]) // Resolve records by id
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const pendingUploads = useAppSelector((state: RootState) => selectAgentConversationFormPendingUserFileUploads(state, { conversationId: chatId }))
  // Split in-flight uploads by where they'll land, so each renders in the right slot from the first frame.
  const pendingUserFileUploads = pendingUploads.filter(({ target }) => target === 'user-files').flatMap(({ uploadState }) => (uploadState ? [uploadState] : []))
  const pendingWritingSampleUploads = pendingUploads
    .filter(({ target }) => target === 'writing-sample')
    .flatMap(({ uploadState }) => (uploadState ? [uploadState] : []))

  // Processing statuses for all files
  const selectedFileProcessingStatuses = useAppSelector((state: RootState) =>
    selectProcessingStatusesForSelectedFiles(state, {
      keys: selectedFilePaths,
      chatId: chatId,
    })
  )

  const selectedFileProcessingStatusesArray = Object.entries(selectedFileProcessingStatuses).filter(([key]) => selectFilePaths.includes(key)) ?? []
  const selectedTemplateFileProcessingStatusesArray = Object.entries(selectedFileProcessingStatuses).filter(([key]) => templateFiles.includes(key))

  const hasFiles =
    userFileIds.length > 0 ||
    writingSampleFileIds.length > 0 ||
    pendingUserFileUploads.length > 0 ||
    pendingWritingSampleUploads.length > 0 ||
    selectedFileProcessingStatusesArray.length > 0 ||
    selectedTemplateFileProcessingStatusesArray.length > 0

  const isInFlight = (uploadState?: FileUploadState) =>
    !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)

  // Explicit per-list removers: a user-file pill and a writing-sample pill carry the same id shape, so
  // the chip itself names which list it belongs to rather than removeFile guessing (which mis-routed when
  // an id appeared in both lists).
  const removeUserFile = (fileId: string, uploadState?: FileUploadState) => {
    if (isInFlight(uploadState)) cancelUserFileUpload(uploadState!.client.clientTmpId)
    dispatch(AgentConversationFormActions.removeUserFile({ conversationId: chatId, userFileId: fileId }))
  }

  const removeWritingSampleFile = (fileId: string, uploadState?: FileUploadState) => {
    if (isInFlight(uploadState)) cancelUserFileUpload(uploadState!.client.clientTmpId)
    dispatch(AgentConversationFormActions.removeWritingSampleFile({ conversationId: chatId, userFileId: fileId }))
  }

  // Legacy/template files are path-keyed and can't collide with the user-file id lists above.
  const removeFile = (fileKey: string, uploadState?: FileUploadState) => {
    if (isInFlight(uploadState)) {
      cancelUserFileUpload(uploadState!.client.clientTmpId)
    }

    // Delete upload task
    dispatch(removeFileProcessing({ sourcePath: fileKey }))
    // Remove from processing queue
    dispatch(deleteFileUploadTask({ sourcePath: fileKey }))

    // Remove from selected files
    // Check if this is a template file
    if (templateFiles.includes(fileKey)) {
      dispatch(AgentConversationFormActions.removeTemplateFile({ conversationId: chatId, filePath: fileKey }))
    } else {
      dispatch(AgentConversationFormActions.removeFile({ conversationId: chatId, filePath: fileKey }))
    }
  }

  const removePendingUserFileUpload = (clientTmpId: string) => {
    cancelUserFileUpload(clientTmpId)
    dispatch(AgentConversationFormActions.removePendingUserFileUpload({ conversationId: chatId, clientTmpId }))
  }

  return (
    <>
      {(hasFiles || showLegacyFilesResetNotice) && (
        <div className="relative -mt-[6px] -bottom-[6px] pb-[6px] mx-[8px] z-5 bg-brand-neutral-50 border border-b-0 rounded-t-lg opacity-100 transition-opacity duration-300 ease-linear ">
          {showLegacyFilesResetNotice && (
            <div className="p-2">
              <InlineMessage variant="info">This chat has been upgraded and your files have been reset. Add them again below.</InlineMessage>
            </div>
          )}
          {isOverLimit && (
            <div className="rounded-lg bg-yellow-50 px-2 py-1 mx-2 mt-2 grid grid-cols-[40px_auto] items-center">
              <div className={'flex h-7 w-7 text-yellow-700 bg-yellow-50 rounded-lg p-1 ring-4 ring-white items-center justify-center'}>
                <AlertErrorCircle />
              </div>
              <span className={'text-xs text-yellow-900 text-opacity-70'}>
                {/* Large files, or a large number of files may impact the accuracy of {brandedAIFriendlyName}'s response. */}
                Reducing the amount of content may improve response quality.
              </span>
            </div>
          )}
          {/* Use filePath as key (not index or status) to avoid re-renders */}
          {hasFiles && (
            <div className="flex w-full flex-wrap gap-2 p-2 justify-items-start overflow-y-auto max-h-24">
              {/* Selected user-files are mapped by backend file id */}
              {userFileIds.map((fileId) => {
                const uploadState = uploadFilesByServerFileId[fileId]
                const file = uploadState?.backend?.record ?? filesById[fileId]
                const status = file?.processing_status
                const attachmentStatus = getUserFileAttachmentStatus({ file, uploadState, loadError: fetchError && !file })
                const isProcessing = attachmentStatus.status === 'uploading' || attachmentStatus.status === 'processing'
                const isError = attachmentStatus.status === 'error'

                return (
                  <UserFilePill
                    // A file can be attached as both a RAG file and the writing sample, so the same id
                    // renders in both maps — namespace the key so sibling pills stay distinct to React.
                    key={`user-file:${fileId}`}
                    fileName={file?.original_filename ?? uploadState?.client.filename ?? fileId}
                    canView={!isProcessing && !isError && !!file?.viewable_content && status === UserFileProcessingStatus.Completed}
                    isProcessing={isProcessing}
                    isError={isError}
                    errorMessage={attachmentStatus.errorMessage ?? null}
                    onView={() => file && viewUserFile(file, { context: { kind: ViewerOpenContextKind.SELECTED_FILE, conversationId: chatId } })}
                    onRemove={() => removeUserFile(fileId, uploadState)}
                  />
                )
              })}
              {/* Writing samples are matter file ids too, shown with the template-style chip */}
              {writingSampleFileIds.map((fileId) => {
                const uploadState = uploadFilesByServerFileId[fileId]
                const file = uploadState?.backend?.record ?? filesById[fileId]
                const status = file?.processing_status
                const attachmentStatus = getUserFileAttachmentStatus({ file, uploadState, loadError: fetchError && !file })
                const isProcessing = attachmentStatus.status === 'uploading' || attachmentStatus.status === 'processing'
                const isError = attachmentStatus.status === 'error'

                return (
                  <UserFilePill
                    key={`writing-sample:${fileId}`}
                    variant="emphasized"
                    fileName={file?.original_filename ?? uploadState?.client.filename ?? fileId}
                    canView={!isProcessing && !isError && !!file?.viewable_content && status === UserFileProcessingStatus.Completed}
                    isProcessing={isProcessing}
                    isError={isError}
                    errorMessage={attachmentStatus.errorMessage ?? null}
                    onView={() => file && viewUserFile(file, { context: { kind: ViewerOpenContextKind.SELECTED_FILE, conversationId: chatId } })}
                    onRemove={() => removeWritingSampleFile(fileId, uploadState)}
                  />
                )
              })}
              {/* In-flight writing-sample uploads render template-style in the writing-sample slot until they resolve */}
              {pendingWritingSampleUploads.map((uploadState) => {
                const attachmentStatus = getUserFileAttachmentStatus({ uploadState })

                return (
                  <UserFilePill
                    key={uploadState.client.clientTmpId}
                    variant="emphasized"
                    fileName={uploadState.client.filename}
                    canView={false}
                    isProcessing={attachmentStatus.status === 'uploading' || attachmentStatus.status === 'processing'}
                    isError={attachmentStatus.status === 'error'}
                    errorMessage={attachmentStatus.errorMessage ?? null}
                    onView={() => undefined}
                    onRemove={() => removePendingUserFileUpload(uploadState.client.clientTmpId)}
                  />
                )
              })}
              {/* Pending user-file uploads are mapped by client tmp id until init-upload succeeds */}
              {pendingUserFileUploads.map((uploadState) => {
                const attachmentStatus = getUserFileAttachmentStatus({ uploadState })

                return (
                  <UserFilePill
                    key={uploadState.client.clientTmpId}
                    fileName={uploadState.client.filename}
                    canView={false}
                    isProcessing={attachmentStatus.status === 'uploading' || attachmentStatus.status === 'processing'}
                    isError={attachmentStatus.status === 'error'}
                    errorMessage={attachmentStatus.errorMessage ?? null}
                    onView={() => undefined}
                    onRemove={() => removePendingUserFileUpload(uploadState.client.clientTmpId)}
                  />
                )
              })}
              {selectedTemplateFileProcessingStatusesArray.map(([filePath, status]) => (
                <FilePill
                  key={filePath}
                  file={[filePath, status as FileProcessingStatus]}
                  isTemplate={true}
                  onRemoveFile={removeFile}
                  conversationId={chatId}
                />
              ))}
              {selectedFileProcessingStatusesArray.map(([filePath, status]) => (
                <FilePill key={filePath} file={[filePath, status as FileProcessingStatus]} onRemoveFile={removeFile} conversationId={chatId} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
